import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';

import { DEFAULT_BODY, DEFAULT_HEADING } from '@/hooks/useDefaultDisclaimers';

import {
  TenantPresentationConfigurationContext,
  useUnguardedTenantPresentationConfigurationContext,
} from '../context/tenantPresentationConfiguration.context';

function getFooterDisclaimers(
  variant: DisclaimerFooterVariant,
  legalDisclaimers: TenantPresentationConfigurationContext['legalDisclaimers']
) {
  // something is defined; use what's defined and nothing else. it's probably okay for someone to just define
  // a header and not a body
  if (
    variant === 'standard' &&
    (legalDisclaimers?.presentationFooterDisclaimerBody ||
      legalDisclaimers?.presentationFooterDisclaimerTitle)
  ) {
    return {
      heading: legalDisclaimers?.presentationFooterDisclaimerTitle,
      body: legalDisclaimers?.presentationFooterDisclaimerBody,
    };
  }

  if (
    variant === 'cover' &&
    (legalDisclaimers?.presentationCoverDisclaimerBody ||
      legalDisclaimers?.presentationCoverDisclaimerTitle)
  ) {
    return {
      heading: legalDisclaimers?.presentationCoverDisclaimerTitle,
      body: legalDisclaimers?.presentationCoverDisclaimerBody,
    };
  }

  return {
    heading: DEFAULT_HEADING,
    body: DEFAULT_BODY,
  };
}

type DisclaimerFooterVariant = 'cover' | 'standard';
type DisclaimerFooterSizing = 'fixed' | 'client-presentation';

type DisclaimerFontSizePx = 8 | 9 | 10;

// Leave space below the measured client-presentation limits so differences in
// word and glyph widths do not increase the fixed footer height.
const CLIENT_PRESENTATION_LARGE_DISCLAIMER_MAX_LENGTH = 300;
const CLIENT_PRESENTATION_MEDIUM_DISCLAIMER_MAX_LENGTH = 380;

export function getClientPresentationDisclaimerFontSizePx(
  bodyLength: number
): DisclaimerFontSizePx {
  if (bodyLength <= CLIENT_PRESENTATION_LARGE_DISCLAIMER_MAX_LENGTH) {
    return 10;
  }

  if (bodyLength <= CLIENT_PRESENTATION_MEDIUM_DISCLAIMER_MAX_LENGTH) {
    return 9;
  }

  return 8;
}

export interface DisclaimerFooterProps {
  contentSx?: SystemStyleObject<Theme>;
  // we allow users to define different copy for the cover slide
  variant: DisclaimerFooterVariant;
  sizing?: DisclaimerFooterSizing;
}

export function DisclaimerFooter({
  variant,
  contentSx = {},
  sizing = 'fixed',
}: DisclaimerFooterProps) {
  const { legalDisclaimers } =
    useUnguardedTenantPresentationConfigurationContext();

  const { heading, body } = getFooterDisclaimers(variant, legalDisclaimers);
  const bodyFontSizePx =
    sizing === 'client-presentation'
      ? getClientPresentationDisclaimerFontSizePx(body?.length ?? 0)
      : 8;
  const titleFontSizePx = bodyFontSizePx - 2;
  const lineHeightPx = bodyFontSizePx + 2;

  return (
    <Box>
      {heading && (
        <Typography
          component="div"
          variant="body-xs"
          sx={(theme) => ({
            fontWeight: theme.typography.fontWeightBold,
            textTransform: 'uppercase',
            fontSize: `${titleFontSizePx}px`,
            lineHeight: `${lineHeightPx}px`,
            ...(contentSx || {}),
          })}
        >
          {heading}
        </Typography>
      )}
      {body && (
        <Typography
          component="div"
          variant="body-xs"
          sx={{
            fontSize: `${bodyFontSizePx}px`,
            lineHeight: `${lineHeightPx}px`,
            ...contentSx,
          }}
        >
          {body}
        </Typography>
      )}
    </Box>
  );
}
